import { useTranslation, useIsRTL, useState, useCallback, type ReactNode } from '@ufc/core'
import { BaseDrawer, Badge, Button, cn } from '@ufc/uikit'
import { Copy, Check, ClipboardCopy } from '@ufc/uikit'
import type { SapSyncJob } from '../../domain/types'
import { SAP_SYNC_STATUS } from '../../domain/constants'
import { formatJson, formatDuration, buildCurlCommand } from '../utils/formatters'

interface SyncJobDetailDrawerProps {
  job: SapSyncJob | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

type DrawerTab = 'overview' | 'http' | 'request' | 'response' | 'error'

const STATUS_GRADIENT: Record<string, string> = {
  [SAP_SYNC_STATUS.COMPLETED]: 'from-emerald-500 to-teal-500',
  [SAP_SYNC_STATUS.PROCESSING]: 'from-blue-500 to-cyan-500',
  [SAP_SYNC_STATUS.PENDING]: 'from-slate-500 to-slate-400',
  [SAP_SYNC_STATUS.FAILED]: 'from-red-500 to-rose-500',
  [SAP_SYNC_STATUS.DLQ]: 'from-orange-500 to-amber-500',
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between py-2 border-b last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-end">{value}</span>
    </div>
  )
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [text])

  return (
    <Button variant="ghost" size="sm" onClick={handleCopy} className="h-7 gap-1.5 text-xs">
      {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? label : label}
    </Button>
  )
}

function JsonBlock({ title, data, t }: { title: string; data: unknown; t: (key: string, fallback: string) => string }) {
  const text = formatJson(data)
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium">{title}</h4>
        {text !== '-' && (
          <CopyButton text={text} label={t('sapSync.copyToClipboard', 'Copy')} />
        )}
      </div>
      <pre className="rounded-lg bg-muted p-3 text-xs overflow-auto max-h-96 font-mono">
        {text}
      </pre>
    </div>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex-1 px-4 py-3 text-sm font-medium inline-flex items-center justify-center relative transition-[background-color,color]',
        active
          ? 'bg-primary/15 text-primary font-semibold'
          : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50',
      )}
    >
      {children}
      {active && (
        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
      )}
    </button>
  )
}

export function SyncJobDetailDrawer({ job, open, onOpenChange }: SyncJobDetailDrawerProps) {
  const { t } = useTranslation()
  const isRTL = useIsRTL()
  const [activeTab, setActiveTab] = useState<DrawerTab>('overview')

  if (!job) return null

  const gradient = STATUS_GRADIENT[job.status] || STATUS_GRADIENT[SAP_SYNC_STATUS.PENDING]
  const showErrorTab = job.status === 'failed' || job.status === 'dlq'
  const curlCommand = buildCurlCommand(job)

  const headerContent = (
    <div className="flex items-center gap-3">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm">
            {job.status}
          </Badge>
          <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm">
            {job.direction}
          </Badge>
          {job.httpMethod && (
            <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm font-mono">
              {job.httpMethod}
            </Badge>
          )}
        </div>
        <h2 className="text-lg font-bold text-white">
          {t('sapSync.jobDetail', 'Sync Job Detail')}
        </h2>
        <p className="text-sm text-white/70 font-mono">{job.documentId}</p>
      </div>
    </div>
  )

  const stickyContent = (
    <div className="px-6 py-3 bg-slate-50/50 dark:bg-slate-800/30 border-b border-border/20">
      <div className="glass-card rounded-2xl overflow-hidden border border-border/50 flex">
        <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
          {t('sapSync.overview', 'Overview')}
        </TabButton>
        <TabButton active={activeTab === 'http'} onClick={() => setActiveTab('http')}>
          {t('sapSync.httpDetails', 'HTTP')}
        </TabButton>
        <TabButton active={activeTab === 'request'} onClick={() => setActiveTab('request')}>
          {t('sapSync.request', 'Request')}
        </TabButton>
        <TabButton active={activeTab === 'response'} onClick={() => setActiveTab('response')}>
          {t('sapSync.response', 'Response')}
        </TabButton>
        {showErrorTab && (
          <TabButton active={activeTab === 'error'} onClick={() => setActiveTab('error')}>
            {t('sapSync.error', 'Error')}
          </TabButton>
        )}
      </div>
    </div>
  )

  return (
    <BaseDrawer
      open={open}
      onOpenChange={onOpenChange}
      isRTL={isRTL}
      headerGradient={gradient}
      headerContent={headerContent}
      stickyContent={stickyContent}
      fixedWidth={560}
    >
      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-0">
          <DetailRow label={t('sapSync.entityType', 'Entity Type')} value={job.entityType} />
          <DetailRow label={t('sapSync.entityId', 'Entity ID')} value={
            <span className="font-mono text-xs">{job.entityId}</span>
          } />
          <DetailRow label={t('sapSync.sapOperation', 'Operation')} value={job.sapOperation || '-'} />
          <DetailRow label={t('sapSync.sapDoc', 'SAP Doc #')} value={
            <span className="font-mono text-xs">{job.sapDocumentNumber || '-'}</span>
          } />
          <DetailRow label={t('sapSync.executionTime', 'Execution Time')} value={formatDuration(job.durationMs)} />
          <DetailRow label={t('sapSync.correlationId', 'Correlation ID')} value={
            <span className="font-mono text-xs">{job.correlationId || '-'}</span>
          } />
          <DetailRow label={t('sapSync.tenantId', 'Tenant ID')} value={job.tenantId || '-'} />
          <DetailRow label={t('sapSync.eventId', 'Event ID')} value={
            <span className="font-mono text-xs">{job.eventId || '-'}</span>
          } />
          <DetailRow label={t('sapSync.attempts', 'Attempts')} value={job.attempts} />
          <DetailRow label={t('sapSync.createdAt', 'Created')} value={new Date(job.createdAt).toLocaleString()} />
          {job.lastAttemptAt && (
            <DetailRow label={t('sapSync.lastAttempt', 'Last Attempt')} value={new Date(job.lastAttemptAt).toLocaleString()} />
          )}
          {job.completedAt && (
            <DetailRow label={t('sapSync.completedAt', 'Completed')} value={new Date(job.completedAt).toLocaleString()} />
          )}
        </div>
      )}

      {/* HTTP Details Tab */}
      {activeTab === 'http' && (
        <div className="space-y-3">
          <div className="space-y-0">
            <DetailRow label={t('sapSync.httpMethod', 'Method')} value={
              job.httpMethod
                ? <Badge variant="outline" className="font-mono text-xs">{job.httpMethod}</Badge>
                : '-'
            } />
            <DetailRow label={t('sapSync.url', 'URL')} value={
              job.url
                ? <span className="font-mono text-xs break-all">{job.url}</span>
                : '-'
            } />
            <DetailRow label={t('sapSync.responseStatus', 'Status Code')} value={
              job.responseStatus != null
                ? <Badge
                    variant="outline"
                    className={cn(
                      'font-mono text-xs',
                      job.responseStatus >= 200 && job.responseStatus < 300
                        ? 'border-green-500 text-green-700'
                        : 'border-red-500 text-red-700',
                    )}
                  >
                    {job.responseStatus}
                  </Badge>
                : '-'
            } />
            <DetailRow label={t('sapSync.executionTime', 'Execution Time')} value={formatDuration(job.durationMs)} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium flex items-center gap-1.5">
                <ClipboardCopy className="h-4 w-4" />
                cURL
              </h4>
              {curlCommand && (
                <CopyButton text={curlCommand} label={t('sapSync.exportAsCurl', 'Copy as cURL')} />
              )}
            </div>
            <pre className="rounded-lg bg-muted p-3 text-xs overflow-auto max-h-48 font-mono">
              {curlCommand || '-'}
            </pre>
          </div>

          {job.requestHeaders && Object.keys(job.requestHeaders).length > 0 && (
            <JsonBlock title={t('sapSync.requestHeaders', 'Request Headers')} data={job.requestHeaders} t={t} />
          )}

          {job.responseHeaders && Object.keys(job.responseHeaders).length > 0 && (
            <JsonBlock title={t('sapSync.responseHeaders', 'Response Headers')} data={job.responseHeaders} t={t} />
          )}
        </div>
      )}

      {/* Request Tab */}
      {activeTab === 'request' && (
        <div className="space-y-3">
          {job.httpMethod && (
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className="font-mono text-xs">{job.httpMethod}</Badge>
              {job.url && <span className="font-mono text-xs text-muted-foreground break-all">{job.url}</span>}
            </div>
          )}

          {curlCommand && (
            <div className="flex justify-end">
              <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs" onClick={() => {
                navigator.clipboard.writeText(curlCommand)
              }}>
                <ClipboardCopy className="h-3.5 w-3.5" />
                {t('sapSync.exportAsCurl', 'Copy as cURL')}
              </Button>
            </div>
          )}

          {job.requestHeaders && Object.keys(job.requestHeaders).length > 0 && (
            <JsonBlock title={t('sapSync.requestHeaders', 'Request Headers')} data={job.requestHeaders} t={t} />
          )}
          <JsonBlock title={t('sapSync.requestBody', 'Request Body')} data={job.requestPayload} t={t} />
        </div>
      )}

      {/* Response Tab */}
      {activeTab === 'response' && (
        <div className="space-y-3">
          {job.responseStatus != null && (
            <div className="flex items-center gap-2 mb-2">
              <Badge
                variant="outline"
                className={cn(
                  'font-mono text-xs',
                  job.responseStatus >= 200 && job.responseStatus < 300
                    ? 'border-green-500 text-green-700'
                    : 'border-red-500 text-red-700',
                )}
              >
                {job.responseStatus}
              </Badge>
              {job.durationMs != null && (
                <span className="text-xs text-muted-foreground">{formatDuration(job.durationMs)}</span>
              )}
            </div>
          )}
          {job.responseHeaders && Object.keys(job.responseHeaders).length > 0 && (
            <JsonBlock title={t('sapSync.responseHeaders', 'Response Headers')} data={job.responseHeaders} t={t} />
          )}
          <JsonBlock title={t('sapSync.responseBody', 'Response Body')} data={job.responsePayload} t={t} />
        </div>
      )}

      {/* Error Tab */}
      {activeTab === 'error' && showErrorTab && (
        <div className="space-y-3">
          {job.errorCode && (
            <div>
              <span className="text-sm text-muted-foreground">{t('sapSync.errorCode', 'Error Code')}</span>
              <div className="mt-1">
                <Badge variant="destructive">{job.errorCode}</Badge>
              </div>
            </div>
          )}
          {job.errorMessage && (
            <div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('sapSync.errorMessage', 'Error')}</span>
                <CopyButton text={job.errorMessage} label={t('sapSync.copyToClipboard', 'Copy')} />
              </div>
              <div className="mt-1 rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-800 dark:text-red-200">
                {job.errorMessage}
              </div>
            </div>
          )}
        </div>
      )}
    </BaseDrawer>
  )
}
